<template>
  <div class="waw-confirm">
    <p v-if="readOnly" class="waw-locked">
      These details are saved and can&rsquo;t be changed here. If the name needs to change, contact
      Teamio &mdash; WhatsApp has to review any change to it.
    </p>

    <label class="waw-field">
      <span class="waw-field__label">The name patients will see on your messages</span>
      <input
        type="text"
        class="waw-input"
        :class="{ 'is-invalid': showLengthError }"
        :value="answers.displayName"
        :maxlength="maxLength"
        :disabled="readOnly"
        placeholder="e.g. Mint Dental of Emerson"
        data-test="waw-confirm-display-name"
        :aria-describedby="showLengthError ? 'waw-confirm-display-name-error' : null"
        @input="onName($event.target.value)"
      >
      <span class="waw-field__hint">
        This is what shows above every message you send, so use the name your patients already know
        you by. Between {{ minLength }} and {{ maxLength }} characters.
      </span>
      <span
        v-if="showLengthError"
        id="waw-confirm-display-name-error"
        class="waw-field__error"
        data-test="waw-confirm-display-name-error"
      >
        {{ lengthError }}
      </span>
    </label>

    <label class="waw-consent">
      <input
        type="checkbox"
        class="waw-consent__box"
        :checked="answers.consentAcknowledged === true"
        :disabled="readOnly"
        data-test="waw-confirm-consent"
        @change="onConsent($event.target.checked)"
      >
      <span class="waw-consent__text">
        I confirm the patients we&rsquo;ll message on WhatsApp have given our practice permission to
        contact them.
      </span>
    </label>
  </div>
</template>

<script>
/**
 * Wizard screen 3 — the display name WhatsApp reviews, and the permission acknowledgment.
 *
 * ── WHY THE LENGTH IS ENFORCED HERE AND NOT ONLY SERVER-SIDE ─────────────────────────────────────
 * WhatsApp reviews this name, and a rejection costs a review cycle measured in days. The backend
 * refuses a bad one (`validateTriageAnswers` → `display_name_invalid`), but a refusal after the
 * click is a worse experience than a Continue button that is visibly not ready, with the reason
 * next to the field. `maxlength` on the input makes the upper bound unreachable by typing; the
 * error text covers paste and the lower bound.
 *
 * ── WHY THE CHECKBOX IS A HARD GATE ──────────────────────────────────────────────────────────────
 * A stored consent that was never actually given is worse than no record at all: it is the artifact
 * we would point at if WhatsApp ever asked how these patients opted in. Continue stays disabled
 * until it is ticked (the shell's `isWizardStepValid`), so nothing is saved that we would have to
 * defend later.
 *
 * ── PREFILL ──────────────────────────────────────────────────────────────────────────────────────
 * The shell seeds `displayName` from the practice's name on the account, and from the server's echo
 * once triage has been submitted. This screen never invents a default of its own — a name the
 * practice did not choose is exactly the thing that gets rejected.
 */
import { DISPLAY_NAME_MIN, DISPLAY_NAME_MAX } from '@/store/whatsappSelfStart';

export default {
  name: 'WizardConfirmStep',

  props: {
    answers: { type: Object, required: true },
    readOnly: { type: Boolean, default: false },
  },

  computed: {
    minLength() { return DISPLAY_NAME_MIN; },
    maxLength() { return DISPLAY_NAME_MAX; },

    trimmedName() {
      return typeof this.answers.displayName === 'string' ? this.answers.displayName.trim() : '';
    },

    /**
     * Only complain once they have typed something. An empty field on arrival is not a mistake
     * yet — the disabled Continue already says the screen is not finished.
     */
    showLengthError() {
      return !this.readOnly && this.trimmedName !== '' && this.lengthError !== '';
    },

    lengthError() {
      const n = this.trimmedName.length;
      if (n !== 0 && n < DISPLAY_NAME_MIN) {
        return `That name is ${n} character${n === 1 ? '' : 's'} — it needs at least ${DISPLAY_NAME_MIN}.`;
      }
      if (n > DISPLAY_NAME_MAX) {
        return `That name is ${n} characters — the most WhatsApp allows is ${DISPLAY_NAME_MAX}.`;
      }
      return '';
    },
  },

  methods: {
    onName(value) {
      if (this.readOnly) return;
      this.$emit('update', { displayName: value });
    },

    onConsent(checked) {
      if (this.readOnly) return;
      // Stored as a strict boolean, never the event's truthiness — the backend stores `true` only.
      this.$emit('update', { consentAcknowledged: checked === true });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-locked {
  margin: 0 0 1.2rem;
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  color: $pp-text-muted;
  font-size: 0.83rem;
  line-height: 1.6;
}

.waw-field {
  display: block;
  max-width: 520px;
  margin-bottom: 1.4rem;
}

.waw-field__label {
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin-bottom: 0.45rem;
}

/* A plain <input>, not b-form-input: it carries this component's scope attribute directly, so the
   styling below cannot leak (repo CSS rule). */
.waw-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.7rem;
  font-size: 0.9rem;
  color: $pp-text;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;

  &:focus {
    outline: none;
    border-color: $pp-brand;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }

  &:disabled {
    background: $pp-surface-sunken;
    color: $pp-text-muted;
  }

  &.is-invalid {
    border-color: $pp-red;
  }
}

.waw-field__hint {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.78rem;
  line-height: 1.55;
  color: $pp-text-subtle;
}

.waw-field__error {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: $pp-red-text;
}

.waw-consent {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  max-width: 640px;
  margin: 0;
  padding: 0.75rem 0.85rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  cursor: pointer;
}

.waw-consent__box {
  margin-top: 0.15rem;
  flex: 0 0 auto;
}

.waw-consent__text {
  font-size: 0.85rem;
  line-height: 1.6;
  color: $pp-text-strong;
}
</style>
